/**
 * RoutineGlyph — the ONE routine-thread marker (Routine Threads, 2026-07-31).
 *
 * A small inline-SVG circular-arrows "repeat" icon rendered wherever a thread
 * (or message) is routine-backed: sidebar rows, switcher chips, the thread
 * header line, the routine manager panel, and the per-run banner chip. One
 * component so every surface agrees on shape and state colors:
 *   • active  → gold (#B6924C — the platform accent)
 *   • paused  → muted gray (the routine is dormant, the thread is not "hot")
 *
 * Pure presentational; no state, no effects. `data-routine-glyph` is the
 * stable test hook; `aria-label="Routine"` announces the marker (the plain
 * word — user-facing copy never says "cron").
 */
import React from 'react';

export const ROUTINE_GLYPH_ACTIVE_COLOR = 'var(--brand-gold-hover)';
export const ROUTINE_GLYPH_PAUSED_COLOR = '#8a8a8a';

export interface RoutineGlyphProps {
  /** True (default) → gold active treatment; false → muted paused gray. */
  active?: boolean;
  /** Square icon size in px (default 13 — sized for row/chip meta lines). */
  size?: number;
}

export const RoutineGlyph: React.FC<RoutineGlyphProps> = ({ active = true, size = 13 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke={active ? ROUTINE_GLYPH_ACTIVE_COLOR : ROUTINE_GLYPH_PAUSED_COLOR}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label="Routine"
    data-routine-glyph=""
    data-routine-active={active ? 'true' : 'false'}
    style={{ flex: 'none', verticalAlign: 'middle' }}
  >
    {/* Circular arrows (repeat): top arc flowing right, bottom arc flowing left. */}
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

// ── routine meta formatting (shared by every routine surface) ───────────────
// Kept beside the glyph so the sidebar rows, chip tooltips, thread header,
// manager panel, and run banner can never disagree about wording. Mirrors the
// compact breakpoints of `relativeActivityLabel` (ThreadSwitcherBar) but for
// FUTURE times ("Next in 2h") and friendlier past phrasing ("5m ago").

/**
 * Friendly relative label for a routine's NEXT run ("Next in 2h",
 * "Next run soon" when due/imminent). Empty string for absent/invalid input —
 * callers fall back to nothing rather than rendering garbage.
 */
export function routineNextRunLabel(iso?: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const diff = ms - nowMs;
  if (diff <= 60_000) return 'Next run soon';
  const min = Math.round(diff / 60_000);
  if (min < 60) return `Next in ${min}m`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `Next in ${hrs}h`;
  return `Next in ${Math.round(hrs / 24)}d`;
}

/**
 * Compact next-run label for the DOCKED header's one meta line (density
 * round, 2026-08-01): "Next in 12m" → "next 12m", "Next run soon" → "next
 * soon". Derived from `routineNextRunLabel` (never a second computation) so
 * the compact and full forms can only ever disagree in casing/prefix.
 */
export function routineNextRunLabelCompact(iso?: string | null, nowMs: number = Date.now()): string {
  const full = routineNextRunLabel(iso, nowMs);
  if (!full) return '';
  if (full === 'Next run soon') return 'next soon';
  return full.replace(/^Next in /, 'next ');
}

/**
 * Compact schedule summary for the DOCKED header's one meta line (density
 * round, 2026-08-01): "Every 15 minutes" → "Every 15 min", "Every weekday at
 * 7:30 AM (Pacific)" → "Every weekday at 7:30 AM". Conservative textual
 * shortening only — a trailing parenthetical (timezone) drops and the long
 * unit words compress; anything unrecognized passes through untouched, so
 * the compact line can never say something the full summary doesn't.
 */
export function compactRoutineScheduleSummary(summary?: string): string {
  if (!summary) return '';
  return summary
    .replace(/\s*\([^()]*\)\s*$/, '')
    .replace(/\bminutes\b/gi, 'min')
    .replace(/\bminute\b/gi, 'min')
    .replace(/\bhours\b/gi, 'hrs')
    .replace(/\bhour\b/gi, 'hr')
    .replace(/\bseconds\b/gi, 'sec')
    .trim();
}

/**
 * NAMING COHERENCE (production feedback 2026-08-01; multi-routine v2
 * 2026-08-16): the ONE rule for what a routine thread is CALLED, shared by
 * the sidebar rows, the thread header label, and the manager panel. A thread
 * holding exactly ONE routine prefers that routine's name (`routineName`)
 * over the thread title — the backend adopts routine names into single-
 * routine thread titles, but no surface may depend on that convergence. A
 * thread holding SEVERAL routines keeps its OWN title (no single routine's
 * name is the thread's to claim). Non-routine threads (and routine threads
 * with a blank name) keep the title.
 */
export function routineThreadDisplayName(thread: {
  routineId?: string;
  routineName?: string;
  routineCount?: number;
  title: string;
}): string {
  if (!thread.routineId) return thread.title;
  if ((thread.routineCount ?? 1) > 1) return thread.title;
  return (thread.routineName || '').trim() || thread.title;
}

/**
 * Friendly relative label for a PAST routine timestamp ("just now",
 * "5m ago", "3h ago", "2d ago") — used for the run banner's scheduled time
 * and the manager panel's last-run column. Empty string for absent/invalid.
 */
export function routinePastTimeLabel(iso?: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const diff = Math.max(0, nowMs - ms);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default RoutineGlyph;
